import { Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays, Clock } from "lucide-react";
import { SiteLayout } from "@/components/SiteLayout";
import { type Locale } from "@/i18n/config";
import { pageHead } from "@/i18n/head";
import { shared } from "@/i18n/shared";
import { blogStrings, dateLocaleTag } from "@/i18n/content/blog";
import { articlePath, postsForLocale, readingMinutes } from "@/data/blog/posts";
import { Img, SIZES } from "@/components/Img";

export function blogIndexHead(locale: Locale) {
  const t = blogStrings[locale];
  return pageHead("artikler", locale, {
    title: t.metaTitle,
    description: t.metaDescription ?? t.indexIntro,
  });
}

export function BlogIndexPage({ locale }: { locale: Locale }) {
  const t = blogStrings[locale];
  const posts = postsForLocale(locale);

  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4261_1350x900.jpg"
            alt={t.indexHeroAlt}
            className="h-full w-full object-cover"
            sizes={SIZES.full}
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/50" />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-20 md:py-32">
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight">{t.indexTitle}</h1>
          <p className="mt-6 max-w-2xl text-lg md:text-xl text-foreground/85 leading-relaxed">{t.indexIntro}</p>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {posts.map((p) => {
            const c = p.content[locale]!;
            return (
              <Link
                key={p.id}
                to={articlePath(p, locale)}
                className="group rounded-2xl bg-secondary overflow-hidden flex flex-col hover:shadow-xl transition-shadow"
              >
                <Img src={p.heroImage} alt={c.heroAlt} className="w-full h-56 object-cover" sizes={SIZES.thumb} />
                <div className="p-7 flex flex-col flex-1">
                  {p.tags?.length ? (
                    <div className="flex flex-wrap gap-2">
                      {p.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold">
                          {tag}
                        </span>
                      ))}
                    </div>
                  ) : null}
                  <h2 className="mt-3 text-xl font-bold group-hover:text-primary transition-colors">{c.title}</h2>
                  <p className="mt-2 text-sm text-foreground/75 leading-relaxed line-clamp-3 flex-1">{c.description}</p>
                  <div className="mt-5 flex items-center gap-x-4 text-xs text-foreground/55">
                    <time dateTime={p.datePublished} className="inline-flex items-center gap-1.5">
                      <CalendarDays className="h-3.5 w-3.5" />
                      {new Date(p.datePublished).toLocaleDateString(dateLocaleTag[locale], { year: "numeric", month: "short", day: "numeric" })}
                    </time>
                    <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t.readingTime(readingMinutes(c))}</span>
                  </div>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    {shared[locale].cta.readMore} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </SiteLayout>
  );
}
